import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { CheckCircle, Sparkles, ArrowRight, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const MemberAdsSidebar = ({ 
  limit = 4, 
  category = null, 
  title = "Featured Members",
  className = "" 
}) => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMembers();
  }, [category, user]);

  const fetchMembers = async () => {
    try {
      const params = { limit };
      if (category) params.category = category;
      if (user?.id) params.exclude_user_id = user.id;

      const response = await axios.get(`${API}/api/promo-pages/sidebar-ads`, { params });
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to load member ads:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="p-4 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (members.length === 0) {
    return null;
  }

  return (
    <Card className={className} data-testid="member-ads-sidebar">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-500" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-0 space-y-3">
        {members.map((member) => (
          <Link 
            key={member.id}
            to={`/members/${member.username}`}
            className="block"
          >
            <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors">
              {member.logo_url ? (
                <img 
                  src={member.logo_url}
                  alt={member.display_name}
                  className="w-10 h-10 rounded-lg object-cover"
                />
              ) : (
                <Avatar className="w-10 h-10">
                  <AvatarImage src={member.avatar_url} />
                  <AvatarFallback>
                    {member.display_name?.charAt(0) || member.username?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <p className="font-medium text-sm truncate">{member.display_name}</p>
                  {member.is_verified && (
                    <CheckCircle className="w-3 h-3 text-blue-500 flex-shrink-0" />
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {member.headline}
                </p>
              </div>
              {member.membership_tier === 'premium' && (
                <Badge variant="secondary" className="text-xs px-1.5 py-0 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700">
                  Pro
                </Badge>
              )}
            </div>
          </Link>
        ))}
      </CardContent>
    </Card>
  );
};

export default MemberAdsSidebar;
